import React, { useState, useMemo } from 'react'
import { usePage } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { ChevronRight } from 'lucide-react'

interface Stage {
  id: number
  name: string
  shortName: string
  modifier: string
  modifierValue: number | null // percentage or null for pass-through
  toggleable: boolean
  type: 'percentage' | 'clamp' | 'round' | 'passthrough'
}

const STAGES: Stage[] = [
  { id: 1, name: 'Base Rate', shortName: 'Base', modifier: '', modifierValue: null, toggleable: false, type: 'passthrough' },
  { id: 2, name: 'Seasonal', shortName: 'Seasonal', modifier: '+15%', modifierValue: 15, toggleable: true, type: 'percentage' },
  { id: 3, name: 'Day of Week', shortName: 'Day/Wk', modifier: '+8%', modifierValue: 8, toggleable: true, type: 'percentage' },
  { id: 4, name: 'Property Demand', shortName: 'Prop Demand', modifier: '-4%', modifierValue: -4, toggleable: true, type: 'percentage' },
  { id: 5, name: 'Room Demand', shortName: 'Room Demand', modifier: '+3%', modifierValue: 3, toggleable: true, type: 'percentage' },
  { id: 6, name: 'Event', shortName: 'Event', modifier: '+5%', modifierValue: 5, toggleable: true, type: 'percentage' },
  { id: 7, name: 'Orphan Day', shortName: 'Orphan', modifier: '0%', modifierValue: 0, toggleable: true, type: 'percentage' },
  { id: 8, name: 'Clamp', shortName: 'Clamp', modifier: '80-150', modifierValue: null, toggleable: false, type: 'clamp' },
  { id: 9, name: 'Rounding', shortName: 'Round', modifier: 'nearest 1', modifierValue: null, toggleable: false, type: 'round' },
  { id: 10, name: 'Override', shortName: 'Override', modifier: 'off', modifierValue: null, toggleable: false, type: 'passthrough' },
  { id: 11, name: 'Rate Plan Offset', shortName: 'Plan Offset', modifier: '0%', modifierValue: null, toggleable: false, type: 'passthrough' },
  { id: 12, name: 'Restrictions', shortName: 'Restrict', modifier: 'off', modifierValue: null, toggleable: false, type: 'passthrough' },
]

const FLOOR = 80
const CEILING = 150

export default function PipelineVisualizer() {
  // Room-Type Demand is globally kill-switched (App::RateEngine::Config); drop it from the
  // visualiser when off so the demo mirrors the stages the engine actually runs. Removing id 5
  // does not renumber the others (Event stays id 6, Clamp id 8, …), so the running-total lookups
  // by id below are unaffected.
  const roomDemandEnabled = usePage().props.roomDemandEnabled === true
  const stages = useMemo(
    () => (roomDemandEnabled ? STAGES : STAGES.filter((s) => s.name !== 'Room Demand')),
    [roomDemandEnabled]
  )
  const [baseRate, setBaseRate] = useState(100)
  const [enabledStages, setEnabledStages] = useState<Record<number, boolean>>({
    2: true,
    3: true,
    4: true,
    5: true,
    6: false,
    7: false,
  })

  const toggleStage = (id: number) => {
    setEnabledStages((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const runningTotals = useMemo(() => {
    const totals: Record<number, { value: number; clamped?: 'floor' | 'ceiling' }> = {}
    let running = baseRate

    for (const stage of stages) {
      if (stage.id === 1) {
        totals[stage.id] = { value: running }
        continue
      }

      if (stage.toggleable && !enabledStages[stage.id]) {
        totals[stage.id] = { value: running }
        continue
      }

      if (stage.type === 'percentage' && stage.modifierValue !== null) {
        running = Math.round((running * (1 + stage.modifierValue / 100)) * 100) / 100
      } else if (stage.type === 'clamp') {
        if (running < FLOOR) {
          totals[stage.id] = { value: FLOOR, clamped: 'floor' }
          running = FLOOR
          continue
        } else if (running > CEILING) {
          totals[stage.id] = { value: CEILING, clamped: 'ceiling' }
          running = CEILING
          continue
        }
      } else if (stage.type === 'round') {
        running = Math.round(running)
      }

      totals[stage.id] = { value: running }
    }

    return totals
  }, [baseRate, enabledStages, stages])

  const finalRate = runningTotals[12]?.value ?? baseRate
  const wasClampedFloor = runningTotals[8]?.clamped === 'floor'
  const wasClampedCeiling = runningTotals[8]?.clamped === 'ceiling'

  return (
    <div className="space-y-5">
      {/* Base rate input */}
      <div className="flex items-center gap-3">
        <Label htmlFor="base-rate-input" className="text-sm font-medium text-foreground whitespace-nowrap">
          Base rate
        </Label>
        <div className="relative w-28">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            £
          </span>
          <Input
            id="base-rate-input"
            type="number"
            min={1}
            max={999}
            value={baseRate}
            onChange={(e) => setBaseRate(Math.max(1, Math.min(999, Number(e.target.value) || 1)))}
            className="h-9 pl-7 text-sm tabular-nums font-semibold"
          />
        </div>
      </div>

      {/* Toggle controls */}
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {stages.filter((s) => s.toggleable).map((stage) => (
          <div key={stage.id} className="flex items-center gap-2">
            <Switch
              id={`toggle-${stage.id}`}
              checked={enabledStages[stage.id] ?? false}
              onCheckedChange={() => toggleStage(stage.id)}
              className="h-5 w-9 data-[state=checked]:bg-primary data-[state=unchecked]:bg-slate-200 [&>span]:h-4 [&>span]:w-4 [&>span]:data-[state=checked]:translate-x-4"
            />
            <Label
              htmlFor={`toggle-${stage.id}`}
              className={cn(
                'text-xs cursor-pointer select-none transition-colors',
                enabledStages[stage.id] ? 'text-foreground font-medium' : 'text-muted-foreground'
              )}
            >
              {stage.name}
            </Label>
          </div>
        ))}
      </div>

      {/* Pipeline flow */}
      <div className="flex flex-wrap items-center gap-y-2">
        {stages.map((stage, idx) => {
          const isActive = !stage.toggleable || enabledStages[stage.id]
          const total = runningTotals[stage.id]
          const isFirst = stage.id === 1
          const isPassthrough = stage.type === 'passthrough' && stage.id !== 1

          return (
            <React.Fragment key={stage.id}>
              {idx > 0 && (
                <ChevronRight className="h-3.5 w-3.5 text-slate-300 mx-0.5 shrink-0" />
              )}
              <div
                className={cn(
                  'flex flex-col items-center rounded-full px-3 py-1.5 transition-all duration-200 border',
                  isActive && !isPassthrough
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : isActive && isPassthrough
                      ? 'bg-slate-50 border-slate-200 text-slate-500'
                      : 'bg-slate-50 border-slate-100 text-slate-300'
                )}
              >
                <span className="text-[10px] font-semibold leading-tight whitespace-nowrap">
                  {stage.shortName}
                </span>
                <span className="text-[10px] tabular-nums leading-tight whitespace-nowrap">
                  {!isActive ? (
                    <span className="text-slate-300">--</span>
                  ) : isFirst ? (
                    <span className="font-semibold">£{baseRate}</span>
                  ) : stage.type === 'clamp' ? (
                    total?.clamped === 'floor' ? (
                      <span className="text-amber-600">floor</span>
                    ) : total?.clamped === 'ceiling' ? (
                      <span className="text-amber-600">ceil</span>
                    ) : (
                      <span className="text-slate-400">pass</span>
                    )
                  ) : stage.type === 'round' ? (
                    <span className="text-slate-400">~1</span>
                  ) : isPassthrough ? (
                    <span className="text-slate-400">pass</span>
                  ) : (
                    <span>{stage.modifier}</span>
                  )}
                </span>
              </div>
            </React.Fragment>
          )
        })}
      </div>

      {/* Running totals strip */}
      <div className="overflow-x-auto">
        <div className="flex items-end gap-0 min-w-max">
          {stages.map((stage, idx) => {
            const isActive = !stage.toggleable || enabledStages[stage.id]
            const total = runningTotals[stage.id]
            const prevTotal = idx > 0 ? runningTotals[stages[idx - 1].id] : null
            const changed = prevTotal && total && Math.abs(total.value - prevTotal.value) > 0.005

            return (
              <div key={stage.id} className="flex flex-col items-center w-[72px]">
                {changed && isActive ? (
                  <span
                    className={cn(
                      'text-[10px] tabular-nums font-medium mb-0.5',
                      total.value > (prevTotal?.value ?? 0) ? 'text-emerald-600' : 'text-rose-600'
                    )}
                  >
                    {total.value > (prevTotal?.value ?? 0) ? '+' : ''}
                    {(total.value - (prevTotal?.value ?? 0)).toFixed(2)}
                  </span>
                ) : (
                  <span className="text-[10px] mb-0.5 invisible">--</span>
                )}
                <div
                  className={cn(
                    'w-full text-center py-1 text-[11px] tabular-nums font-medium border-t transition-all duration-200',
                    isActive
                      ? 'border-primary/20 bg-primary/5 text-foreground'
                      : 'border-slate-100 bg-slate-50/50 text-slate-300'
                  )}
                >
                  {isActive ? `£${total?.value.toFixed(2)}` : '--'}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Final rate display */}
      <div
        className={cn(
          'flex items-center justify-between rounded-lg px-4 py-3 transition-all duration-300',
          wasClampedFloor || wasClampedCeiling
            ? 'bg-amber-50 border border-amber-200'
            : 'bg-primary/5 border border-primary/20'
        )}
      >
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground">Final computed rate</span>
          {(wasClampedFloor || wasClampedCeiling) && (
            <span className="text-[11px] text-amber-600 font-medium">
              {wasClampedFloor
                ? `Clamped to floor (£${FLOOR})`
                : `Clamped to ceiling (£${CEILING})`}
            </span>
          )}
        </div>
        <span className="text-2xl font-bold tabular-nums text-foreground tracking-tight">
          £{finalRate.toFixed(0)}
        </span>
      </div>
    </div>
  )
}
